import {Component, cloneElement} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';

export default class TextArea extends Component {
    static propTypes = {
        /** Additional class(es) to add to the component. */
        className: PropTypes.string,
        /** A unique name which is used to identify this element. If belonging to a form this is used as the key for serialization and is required. */
        name: PropTypes.string,
        /** Text to display in the field before a user starts to type in the textarea. */
        placeholder: PropTypes.string,
        /** A custom label (typically a Label component) to display above the textarea. */
        label: PropTypes.element,
        /** Whether this field is required during validation. */
        required: PropTypes.bool,
        /** Disallows user interaction. */
        disabled: PropTypes.bool,
        autofocus: PropTypes.bool,
        /** The aria-label to apply to the textarea. */
        ariaLabel: PropTypes.string,
        /** A custom validation error (typically a FormElementError component) to display when this component is part of a form and is invalid. */
        error: PropTypes.element,
        /** The default value to display in the textarea when mounted. */
        defaultValue: PropTypes.string,
        /** Specifies the maximum number of characters allowed. */
        maxLength: PropTypes.number,
        /** A custom icon (typically an Icon component) to show in the textarea. */
        icon: PropTypes.element,
        /** A custom help element to place below the input. */
        helpNode: PropTypes.node,
        /**
         * Function to execute when the textarea changes.
         *
         * @param {SyntheticEvent} evt - The React `SyntheticEvent`.
         */
        onChange: PropTypes.func,
        /**
         * Function to execute when the textarea is focused.
         *
         * @param {SyntheticEvent} evt - The React `SyntheticEvent`.
         */
        onFocus: PropTypes.func,
        /**
         * Function to execute when the textarea loses focused.
         *
         * @param {SyntheticEvent} evt - The React `SyntheticEvent`.
         */
        onBlur: PropTypes.func,
        /**
         * Function to execute when the element is marked as invalid by a validation check.
         *
         * @param {ValidityState} validationState - ValidityState object for more fine grained control over error handling in a parent component if desired.
         */
        onInvalid: PropTypes.func,
    };
    state = {
        validationState: null,
    };

    constructor(props) {
        super(props);

        this._ID = `TextArea-${uuidV4()}`;
    }

    componentDidMount() {
        this._textarea.addEventListener('invalid', this._onInvalid);
    }

    componentWillUnmount() {
        this._textarea.removeEventListener('invalid', this._onInvalid);
    }

    _onInvalid = evt => {
        evt.preventDefault();

        const {onInvalid} = this.props;
        const validationState = evt.currentTarget.validity;

        this.setState({
            validationState,
        });

        if (onInvalid) {
            onInvalid(validationState);
        }
    };

    _onChange = evt => {
        const {onChange} = this.props;

        this.setState({
            validationState: null,
        });

        if (onChange) {
            onChange(evt);
        }
    };

    render() {
        const {
            className,
            name,
            label,
            placeholder,
            autofocus,
            disabled,
            required,
            ariaLabel,
            error,
            defaultValue,
            maxLength,
            icon,
            helpNode,
            onFocus,
            onBlur,
        } = this.props;
        const {validationState} = this.state;
        const isValid = validationState ? validationState.isValid : true;
        const classes = classNames(
            'TextArea',
            'FormElement',
            {'TextArea-with-icon': icon},
            {'FormElement-contains-error': !isValid},
            className
        );

        return (
            <div
                ref={node => {
                    this._container = node;
                }}
                className={classes}
            >
                <div className="FormElement-control-container">
                    {label &&
                        cloneElement(label, {
                            htmlFor: this._ID,
                        })}
                    <div className="FormElement-control">
                        <textarea
                            ref={node => {
                                this._textarea = node;
                            }}
                            id={this._ID}
                            className="FormElement-item"
                            name={name}
                            placeholder={placeholder}
                            required={required}
                            disabled={disabled}
                            autoFocus={autofocus}
                            aria-label={ariaLabel}
                            maxLength={maxLength}
                            defaultValue={defaultValue}
                            onChange={this._onChange}
                            onFocus={onFocus}
                            onBlur={onBlur}
                        />
                        {icon}
                    </div>
                </div>
                {helpNode && (
                    <div className="FormElement-help-text">{helpNode}</div>
                )}
                {!isValid && error}
            </div>
        );
    }
}
